import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api, { mediaUrl } from "@/lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, setQty, removeItem, subtotal, count } = useCart();
  const [crossSell, setCrossSell] = useState([]);
  const navigate = useNavigate();
  const FREE = 200;
  const remaining = Math.max(0, FREE - subtotal);
  const pct = Math.min(100, (subtotal / FREE) * 100);

  useEffect(() => {
    if (open) {
      api.get("/products", { params: { featured: true } })
        .then(({ data }) => setCrossSell(data.filter((p) => !items.some((i) => i.product_id === p.id)).slice(0, 3)))
        .catch(() => {});
    }
  }, [open]); // eslint-disable-line

  return (
    <>
      <div className={`fixed inset-0 z-50 bg-ink/40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={() => setOpen(false)} />
      <aside data-testid="cart-drawer"
             className={`fixed top-0 right-0 z-50 h-screen w-full max-w-md bg-linen border-l hairline flex flex-col transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between px-6 py-5 border-b hairline">
          <h2 className="font-heading text-2xl">Your Bag <span className="text-ink/40 text-lg">({count})</span></h2>
          <button onClick={() => setOpen(false)} className="icon-thin" data-testid="cart-close-btn"><X /></button>
        </div>

        <div className="px-6 py-4 border-b hairline">
          <p className="font-body text-[0.7rem] tracking-wide2 text-ink/70 mb-2">
            {remaining > 0 ? <>You're <span className="text-ink font-medium">${remaining.toFixed(2)}</span> away from complimentary shipping</> : "You've unlocked complimentary shipping ✦"}
          </p>
          <div className="h-1 bg-warm/50"><div className="h-full bg-ink transition-all duration-500" style={{ width: `${pct}%` }} /></div>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-20">
              <ShoppingBag size={40} className="icon-thin text-ink/30" strokeWidth={1} />
              <p className="font-heading text-2xl text-ink/60">Your bag is empty</p>
              <button onClick={() => { setOpen(false); navigate("/shop"); }} className="btn-outline mt-2">Shop the Voyage</button>
            </div>
          ) : (
            <div className="space-y-6">
              {items.map((i) => (
                <div key={`${i.product_id}-${i.size}`} className="flex gap-4" data-testid={`cart-item-${i.slug}`}>
                  <Link to={`/product/${i.slug}`} onClick={() => setOpen(false)} className="w-20 h-28 bg-cream overflow-hidden shrink-0">
                    <img src={mediaUrl(i.image)} alt={i.title} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <h3 className="font-heading text-lg leading-tight">{i.title}</h3>
                      <button onClick={() => removeItem(i.product_id, i.size)} className="text-ink/40 hover:text-ink" data-testid="cart-remove-btn"><X size={15} /></button>
                    </div>
                    <p className="font-body text-[0.7rem] tracking-wide2 text-ink/50 uppercase mt-1">Size {i.size}</p>
                    <div className="flex justify-between items-center mt-3">
                      <div className="flex items-center border hairline">
                        <button onClick={() => setQty(i.product_id, i.size, i.quantity - 1)} className="px-2 py-1 icon-thin" data-testid="cart-qty-decrease"><Minus size={12} /></button>
                        <span className="px-3 text-sm font-body" data-testid="cart-qty-value">{i.quantity}</span>
                        <button onClick={() => setQty(i.product_id, i.size, i.quantity + 1)} className="px-2 py-1 icon-thin" data-testid="cart-qty-increase"><Plus size={12} /></button>
                      </div>
                      <span className="font-body text-sm">${(i.price * i.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}

              {crossSell.length > 0 && (
                <div className="pt-6 border-t hairline">
                  <h3 className="font-body uppercase tracking-luxe text-[0.65rem] mb-4">Complete the Look</h3>
                  <div className="space-y-3">
                    {crossSell.map((p) => (
                      <Link key={p.id} to={`/product/${p.slug}`} onClick={() => setOpen(false)} className="flex gap-3 items-center group" data-testid={`cross-sell-${p.slug}`}>
                        <div className="w-14 h-18 bg-cream overflow-hidden"><img src={mediaUrl(p.images?.[0])} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                        <div className="flex-1"><p className="font-heading text-base leading-tight">{p.title}</p><p className="font-body text-xs text-ink/60">${p.price.toFixed(2)}</p></div>
                        <Plus size={16} className="icon-thin text-ink/50" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t hairline px-6 py-5">
            <div className="flex justify-between mb-4"><span className="font-body uppercase tracking-wide2 text-sm">Subtotal</span><span className="font-heading text-xl" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
            <button data-testid="cart-checkout-btn" onClick={() => { setOpen(false); navigate("/checkout"); }} className="btn-primary w-full">Proceed to Checkout</button>
          </div>
        )}
      </aside>
    </>
  );
}
